import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  styled,
} from '@mui/material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  usePreRegRegistrationDetailsQuery,
  useUpdateRoomAssignmentMutation,
  useAdvancePaymentUpdateMutation,
  useCheckInPreRegMutation,
  useIsXenditQuery,
  useUpdateIsPreCheckinMutation
} from '../generated/graphql';
import { buildPreCheckInUrl } from '../Components/helper/preCheckInUrl';
import { MainWrapper } from '../Components/MainWrapper';
import { ChooseRoom } from './FormComponent/ChooseRoom';
import { Payment } from './FormComponent/Payment';
import { AssignRoom } from './FormComponent/AssignRoom';
import { ConfirmCheckIn } from './FormComponent/ConfirmCheckIn';
import Loading from '../Components/Loading/Loading';
import { Selfie } from './FormComponent/Selfie';
import { ConfirmPayment } from './FormComponent/ConfirmPayment';
import { useSnackBar } from '../Components/Hooks/useSnackBar';
import SnackBarMsg from '../Components/SnackBar/SnackBarMsg';

const StepIconConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#ff9800',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#ff9800',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#d3d3d3',
    borderRadius: 1,
  },
}));

const StepIcon = (props) => {
  const { active, completed } = props;
  return completed ? (
    <CheckCircleIcon style={{ fontSize: '20px', color: '#ff9800' }} />
  ) : active ? (
    <CheckCircleOutlineIcon style={{ fontSize: '20px', color: '#ff9800' }} />
  ) : (
    <CheckCircleOutlineIcon style={{ fontSize: '20px', color: '#d3d3d3' }} />
  );
};

export const PreCheckInFormV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const localstate: any = location.state;

  const routeParams = useParams();
  const queryParameters = new URLSearchParams(window.location.search);
  const status = (queryParameters.get('status') || routeParams.status) as string;
  const orderID = (queryParameters.get('orderId') || routeParams.orderId) as string;
  const cancelledUrl = new URL(window.location.href)?.search?.includes('CANCELLED');
  const IsOnlinePayment = JSON.parse(localStorage.getItem('IsOnlinePayment') as any);

  const { snackFunc, snackBarMessage, openSnackBar, close } = useSnackBar()
  
  const [activeStep, setActiveStep] = useState(1);
  const [assignRoom, setAssignRoom] = useState(false);
  const [roomSelect, setRoomSelect] = useState(null);

  const {
    loading: RegistrationDetailsLoadings,
    data: { PreRegRegistrationDetails } = { PreRegRegistrationDetails: null },
  } = usePreRegRegistrationDetailsQuery({
    variables: {
      HotelID: localstate
        ? localstate?.HotelID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID,
      BookingID: localstate
        ? localstate?.BookingID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
      RegistrationID: localstate
        ? localstate?.RegistrationID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
    },
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      localStorage.setItem('RegistrationDetails', JSON.stringify(data.PreRegRegistrationDetails));
    },
  });

  const {
    data: { IsXendit } = { IsXendit: null },
  } = useIsXenditQuery({
    fetchPolicy: 'no-cache',
    variables: {
      HotelID: localstate
        ? localstate?.HotelID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID
    },
  })

  const [UpdateIsPreCheckin] = useUpdateIsPreCheckinMutation({
    variables: {
      BookingID: localstate
        ? localstate?.BookingID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
      RegistrationID: localstate
        ? localstate?.RegistrationID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
    },
    onCompleted: () => {
      setActiveStep(4);
    },
  });

  const [UpdateRoomAssign, 
    { loading: loadingUpdateRoomAssign, data: UpdateRoomAssignment },
  ] = useUpdateRoomAssignmentMutation({});
  const [AdvancePaymentUpdate, { loading: loadingAdvancePaymentUpdate }] = useAdvancePaymentUpdateMutation({});
  const [
    CheckInPreReg, 
    { loading: CheckInPreRegLoading}
  ] = useCheckInPreRegMutation({
    fetchPolicy: 'no-cache',
    onCompleted: ({ CheckInPreReg }) => {
      if (CheckInPreReg === true) {
        UpdateIsPreCheckin()
      }
    },
    onError: (error: any) => {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.message ||
        error?.message ||
        'Check-in failed';
      snackFunc(message, false);
      // Stay in the flow (don't hard-navigate via handleBack) so the snack
      // remains visible; send the guest back to Choose Room to retry.
      setActiveStep(2);
      setAssignRoom(false);
    },
  })

  const checkoutID = IsXendit 
    ? localStorage.getItem('CheckOutID') 
    : JSON.stringify(localStorage.getItem('CheckOutID'))

  useEffect(() => {
    if (status && IsXendit !== null) {
      const storedAdvPaymentID = localStorage.getItem('AdvPaymentID');
      const advancePaymentID =
        storedAdvPaymentID &&
        storedAdvPaymentID !== 'undefined' &&
        storedAdvPaymentID !== 'null'
          ? storedAdvPaymentID
          : undefined;

      AdvancePaymentUpdate({
        variables: {
          Status: status?.toLowerCase(),
          OrderID: orderID,
          CheckOutID: localStorage.getItem('CheckOutID'),
          ...(advancePaymentID ? { AdvancePaymentID: advancePaymentID } : {}),
          RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
          HotelID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID,
          ReceiptNo: localStorage.getItem('ReceiptNo'),
          AppName: 'PreCheckIn',
          IsXendit: IsXendit
        },
      }).then(() => {
        if (status !== 'SUCCESS') {
          UpdateRoomAssign({
            variables: {
              BookingID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
              RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
              RoomID: '',
              RoomTypeID:
                JSON.parse(localStorage.getItem('SelectedRoom') as any)?.RoomTypeID ||
                JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
            },
          });
          setActiveStep(2);
        } else {
          CheckInPreReg({
            variables: {
              BookingID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
              HotelID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID,
              RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID
            }
          })
        }
      }).catch((err) => {
        const message =
          err?.graphQLErrors?.[0]?.message ||
          err?.networkError?.message ||
          err?.message ||
          'Payment update failed';
        setActiveStep(3);
        snackFunc(message, false);
      });
    }
  }, [status, IsXendit]);

  const handleBack = () => {
    if (activeStep === 1 && assignRoom === true) {
      UpdateRoomAssign({
        variables: {
          BookingID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
          RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
          RoomID: '',
          RoomTypeID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.RoomTypeID,
          IsCompleted: false,
        },
      });
      localStorage.removeItem('SelectedRoom');
      setAssignRoom(false);
    } else if (activeStep === 2 && cancelledUrl) {
      setActiveStep(activeStep - 1);
      navigate('/pre-checkin/form/room');
    } else if (activeStep !== 1) {
      setActiveStep(activeStep - 1);
    } else {
      const hotelID = localstate?.HotelID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID;
      const bookingID = localstate?.BookingID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID;
      window.location.href = buildPreCheckInUrl(hotelID, bookingID);
    }
  };

  // const LArrivalDate = JSON.parse(localStorage.getItem('PreRegInfo') as any)?.ArrivalDate
  // const LPreCheckinAdvTime = JSON.parse(localStorage.getItem('PreRegInfo') as any)?.PreCheckinAdvTime 
  const LNAProcessTime = JSON.parse(localStorage.getItem('HotelDetails') as any)?.NAProcessTime  

  const isSelfCheckin = localstate?.SelfCheckin
  const isSelfChooseRoom = localstate?.SelfChooseRoom
  const PreCheckinAdvTime = localstate?.PreCheckinAdvTime
  const ArrivalDate = localstate?.ArrivalDate
  const isOnlinePayment = localstate?.IsOnlinePayment === true || IsOnlinePayment === true;

  const currentDate = new Date();
  const preCheckinAdvTime = PreCheckinAdvTime || 0
  const arrivalDate = ArrivalDate ? new Date(ArrivalDate) : null
  const windowStartDate = arrivalDate
    ? new Date(arrivalDate.getTime() - preCheckinAdvTime * 60 * 60 * 1000)
    : null
  const naHour = LNAProcessTime ? new Date(LNAProcessTime).getHours() : 0
  const naCutoffDate = arrivalDate
    ? new Date(arrivalDate.getFullYear(), arrivalDate.getMonth(), arrivalDate.getDate() + 1, naHour, 0, 0, 0)
    : null
  const dateControl =
    !!arrivalDate &&
    !!windowStartDate &&
    !!naCutoffDate &&
    currentDate >= windowStartDate &&
    currentDate <= naCutoffDate

    console.log('dateControl 2 >>>', dateControl)
    console.log('currentDate 2 >>>', currentDate)
    console.log('windowStartDate 2 >>>', windowStartDate)
    console.log('naCutoffDate 2 >>>', naCutoffDate)
  
  const stepLabels = 
    dateControl
      ? isOnlinePayment
        ? ['Pre CheckIn', 'Selfie', 'Choose Room', 'Payment', 'Confirm']
        : ['Pre CheckIn', 'Confirm']
      : ['Pre CheckIn', 'Confirm']

  const stepper = (
    <Box display="flex">
      <Stack sx={{ width: '100%' }}>
        <Stepper alternativeLabel activeStep={activeStep} connector={<StepIconConnector />}>
          {stepLabels.map(label => (
            <Step key={label}>
              <StepLabel StepIconComponent={StepIcon}>
                <Typography fontSize="12px" style={{ marginTop: '-10px', lineHeight: '1.2' }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>
    </Box>
  );

  const stepperContent = () => {
    switch (activeStep) {
      case 1:
        if (dateControl && isOnlinePayment) {
          return (
            <Selfie 
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              HotelID={localstate?.HotelID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID}
              RegistrationID={localstate?.RegistrationID || JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID}
              BookingID={localstate?.BookingID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID}
            />
          );
        } else {
          return (
            <ConfirmCheckIn RegistrationDetails={PreRegRegistrationDetails} />
          )
        }
      case 2:
        if (isOnlinePayment && isSelfCheckin === true && assignRoom === false) {
          return (
            <ChooseRoom
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              setAssignRoom={setAssignRoom}
              setRoomSelect={setRoomSelect}
              RegistrationDetails={PreRegRegistrationDetails}
              isSelfChooseRoom={isSelfChooseRoom}
              UpdateRoomAssign={UpdateRoomAssign}
            />
          );
        } else if (isOnlinePayment && isSelfCheckin === true && assignRoom === true) {
          return (
            <AssignRoom
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              roomSelect={roomSelect}
              setRoomSelect={setRoomSelect}
              RegistrationDetails={PreRegRegistrationDetails}
              UpdateRoomAssign={UpdateRoomAssign}
            />
          )
        } else {
          return <ConfirmCheckIn RegistrationDetails={PreRegRegistrationDetails} />;
        }
      case 3:
        return (
          <Payment
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            RegistrationDetails={PreRegRegistrationDetails}
          />
        );
      case 4:
        return <ConfirmPayment RegistrationDetails={PreRegRegistrationDetails} />;
      default:
        return null;
    }
  };

  const isLoading =
    RegistrationDetailsLoadings ||
    loadingAdvancePaymentUpdate ||
    CheckInPreRegLoading;

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <MainWrapper>
          <Box
            sx={{ p: 2, pb: '96px', background: 'transparent', width: '100%', maxWidth: '800px' }}
            margin="auto"
            bgcolor="#F7FAF7"
            display="flex"
            flexDirection="column"
            minHeight="90vh"
          >
            {isOnlinePayment && activeStep !== 3 && (
              <Box display="flex" marginBottom="15px" alignItems="center">
                <IconButton
                  style={{ padding: '0px', color: 'black' }}
                  onClick={handleBack}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <Typography fontSize="15px" fontWeight={600} paddingLeft="5px">
                  {PreRegRegistrationDetails?.Guest?.Contact?.FullName}
                </Typography>
              </Box>
            )}
            <Card
              sx={{
                padding: '0px',
                margin: '0 0 14px 0',
                borderRadius: '5px',
                boxShadow:
                  '0 3px 1px -2px transparent, 2px 4px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
              }}
            >
              <CardContent sx={{ ':last-child': { p: '10px' } }} style={{ padding: '7px' }}>
                {stepper}
              </CardContent>
            </Card>
            {stepperContent()}
          </Box>
        </MainWrapper>
      )}

      {/* Keep mounted even during Loading so snack messages are not unmounted. */}
      <SnackBarMsg open={openSnackBar} message={snackBarMessage} onClose={close} />
    </>
  );
};
